import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

export const LANDING_DEFAULT = {
    section_title: "天珠专区",
    section_subtitle: "每一颗，都有它的愿力",
    grid_cols: 3,
    max_items: 0,
    show_all_tab: true,
    show_search: false,
    only_featured: false,
    categories: [],
    review_photos: [],
    hero_eyebrow: "EST. 1989 — MALAYSIA",
    hero_title_1: "珠承天意，",
    hero_title_2: "缘自福来。",
    hero_subtitle: "",
    hero_image: "",
    logo_url: "",
    hero_caption: "No.01 — Radiance Collection",
    hero_cta_primary: "选购天珠",
    hero_cta_secondary: "品牌故事",
    site_title: "福缘珠宝 Fuyuan Jewelry — 臻品现货发售",
    marquee_items: [],
    show_marquee: true,
    show_manifesto: true,
    manifesto_title: "品牌故事",
    manifesto_text: "",
    manifesto_chapters: [],
    footer_phone: "",
    footer_address: "",
    footer_hours: "",
    social_facebook: "",
    social_instagram: "",
    social_tiktok: "",
    shop_shopee: "",
    shop_lazada: "",
    product_fallback_desc: "现货臻品，详情请洽店员。",
    product_stock_template: "现货 {stock} 件 · 与门店库存实时同步",
    product_soldout_text: "暂时售罄",
    footer_brand_title: "福缘珠宝",
    footer_brand_sub: "Fuyuan Jewelry — since 1989",
    footer_copyright: "© 2026 福缘珠宝 FUYUAN JEWELRY. 版权所有",
    footer_tagline: "Crafted with care in Malaysia",
    seo_description: "福缘珠宝 Fuyuan Jewelry — 老天珠、水晶、翡翠，每一件都是时间与心意的结晶。现货发售，下单即锁定库存。",
    seo_keywords: ["老天珠", "财运天珠", "生意天珠", "护法天珠", "缘分天珠", "水晶", "翡翠", "吉隆坡珠宝"],
    google_site_verification: "",
    whatsapp_phone: "+60123456789",
    whatsapp_message: "您好，我刚在福缘珠宝下单，订单号 {invoice_no}，想咨询一下发货 / 付款细节。",
};

const GRID_VALUES = [2, 3, 4, 5];

export function normalizeLandingConfig(data) {
    return {
        section_title: data.section_title || LANDING_DEFAULT.section_title,
        section_subtitle: data.section_subtitle || "",
        grid_cols: GRID_VALUES.includes(data.grid_cols) ? data.grid_cols : 3,
        max_items: Number(data.max_items || 0),
        show_all_tab: data.show_all_tab !== false,
        show_search: !!data.show_search,
        only_featured: !!data.only_featured,
        review_photos: Array.isArray(data.review_photos) ? data.review_photos : [],
        categories: Array.isArray(data.categories)
            ? data.categories.map((c) => ({
                ...c,
                icon: c.icon || "",
                emoji: c.emoji || "",
            }))
            : [],
        hero_eyebrow: data.hero_eyebrow || "",
        hero_title_1: data.hero_title_1 || "",
        hero_title_2: data.hero_title_2 || "",
        hero_subtitle: data.hero_subtitle || "",
        hero_image: data.hero_image || "",
        logo_url: data.logo_url || "",
        hero_caption: data.hero_caption || "",
        hero_cta_primary: data.hero_cta_primary || "",
        hero_cta_secondary: data.hero_cta_secondary || "",
        site_title: data.site_title || "",
        marquee_items: Array.isArray(data.marquee_items) ? data.marquee_items : [],
        show_marquee: data.show_marquee !== false,
        show_manifesto: data.show_manifesto !== false,
        manifesto_title: data.manifesto_title || "",
        manifesto_text: data.manifesto_text || "",
        manifesto_chapters: Array.isArray(data.manifesto_chapters) ? data.manifesto_chapters : [],
        footer_phone: data.footer_phone || "",
        footer_address: data.footer_address || "",
        footer_hours: data.footer_hours || "",
        social_facebook: data.social_facebook || "",
        social_instagram: data.social_instagram || "",
        social_tiktok: data.social_tiktok || "",
        shop_shopee: data.shop_shopee || "",
        shop_lazada: data.shop_lazada || "",
        product_fallback_desc: data.product_fallback_desc || "",
        product_stock_template: data.product_stock_template || "",
        product_soldout_text: data.product_soldout_text || "",
        footer_brand_title: data.footer_brand_title || "",
        footer_brand_sub: data.footer_brand_sub || "",
        footer_copyright: data.footer_copyright || "",
        footer_tagline: data.footer_tagline || "",
        seo_description: data.seo_description || "",
        seo_keywords: Array.isArray(data.seo_keywords) ? data.seo_keywords : [],
        google_site_verification: data.google_site_verification || "",
        whatsapp_phone: data.whatsapp_phone || "",
        whatsapp_message: data.whatsapp_message || "",
    };
}

/**
 * Shared hook for all landing-config sub-pages.
 * Each page loads the full config, edits its own slice, and PUTs the entire object.
 * Backend API contract stays unchanged (`GET/PUT /api/admin/landing-config`).
 */
export function useLandingConfig() {
    const [config, setConfig] = useState(LANDING_DEFAULT);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/landing-config")
            .then(({ data }) => setConfig(normalizeLandingConfig(data)))
            .catch((err) => toast.error(err.response?.data?.detail || "加载失败"))
            .finally(() => setLoading(false));
    }, []);

    const save = async (validate) => {
        if (validate && !validate(config)) return false;
        setSaving(true);
        try {
            const payload = {
                ...config,
                marquee_items: (config.marquee_items || []).filter((x) => x && x.trim()),
                manifesto_chapters: (config.manifesto_chapters || [])
                    .map((ch) => ({
                        no: (ch.no || "").trim(),
                        title: (ch.title || "").trim(),
                        text: (ch.text || "").trim(),
                    }))
                    .filter((ch) => ch.title || ch.text || ch.no),
                review_photos: (config.review_photos || []).filter(Boolean),
                seo_keywords: (config.seo_keywords || [])
                    .map((x) => (x || "").trim())
                    .filter(Boolean),
            };
            const { data } = await api.put("/admin/landing-config", payload);
            setConfig(normalizeLandingConfig(data));
            toast.success("已保存 · 落地页会立即读取最新配置");
            return true;
        } catch (err) {
            toast.error(err.response?.data?.detail || "保存失败");
            return false;
        } finally {
            setSaving(false);
        }
    };

    return { config, setConfig, loading, saving, save };
}
